import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  MenuItem,
  TextField,
  CircularProgress,
} from "@mui/material";
import Header from "../../component/users/Header";
import Footer from "../../component/users/Footer";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CompetitionLeaderboard from "../../component/users/competitions/CompetitionLeaderboard";

const filterSx = {
  minWidth: 150,
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    bgcolor: "rgba(255,255,255,0.05)",
    borderRadius: 2,
    "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
  },
  "& .MuiSvgIcon-root": { color: "#fff" },
};

function leaderboardCategoryTitle(competition, categoryFilter) {
  const selected = String(categoryFilter || "").trim();
  if (selected) return selected;
  const cats = (competition?.categories || [])
    .map((c) => c?.category)
    .filter(Boolean);
  if (cats.length === 1) return cats[0];
  return competition?.name || "";
}

export default function CompetitionLeaderboardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem("mynetgoals.com-token");
  const [competition, setCompetition] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lbLoading, setLbLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState(
    () => searchParams.get("category") || ""
  );
  const [eventFilter, setEventFilter] = useState("");
  const didDefaultCategory = useRef(false);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL_USER}/getCompetitionDetail/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.success) setCompetition(res.data.data);
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, token]);

  useEffect(() => {
    if (!competition || didDefaultCategory.current) return;
    const cats = (competition.categories || [])
      .map((c) => c?.category)
      .filter(Boolean);
    if (!cats.length) return;
    didDefaultCategory.current = true;
    if (categoryFilter && cats.includes(categoryFilter)) return;
    const fromUrl = searchParams.get("category");
    if (fromUrl && cats.includes(fromUrl)) {
      setCategoryFilter(fromUrl);
      return;
    }
    setCategoryFilter(cats[0]);
  }, [competition, categoryFilter, searchParams]);

  const loadLeaderboard = useCallback(async () => {
    if (!token || !id) return;
    setLbLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.set("category", categoryFilter);
      if (eventFilter) params.set("event_id", eventFilter);
      const qs = params.toString();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL_USER}/getCompetitionLeaderboard/${id}${
          qs ? `?${qs}` : ""
        }`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setRows(
          (res.data.data || []).map((r) => ({
            ...r,
            voteCount: r.voteCount ?? r.vote_count ?? 0,
          }))
        );
      } else {
        toast.error(res.data.message || "Failed to load leaderboard");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load leaderboard");
    } finally {
      setLbLoading(false);
    }
  }, [token, id, categoryFilter, eventFilter]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  const boardTitle = useMemo(
    () => leaderboardCategoryTitle(competition, categoryFilter),
    [competition, categoryFilter]
  );

  return (
    <>
      <ToastContainer />
      <Header />
      <Box className="userLayout">
        <Container
          disableGutters
          maxWidth="lg"
          sx={{
            background: "#242743",
            minHeight: "85vh",
            pb: 4,
            width: "100%",
            overflowX: "hidden",
          }}
        >
          <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 2.5 }}>
            <Button
              startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate(`/competitions/${id}`)}
              sx={{
                color: "rgba(255,255,255,0.75)",
                textTransform: "none",
                mb: 2,
              }}
            >
              Back to competition
            </Button>

            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: { xs: 22, sm: 26, md: 30 },
                fontFamily: "Montserrat, sans-serif",
                mb: 0.5,
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              {boardTitle || "Leaderboard"}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", mb: 2.5, fontSize: { xs: 13, sm: 16 } }}>
              {competition?.name && boardTitle !== competition.name
                ? `${competition.name} · `
                : ""}
              contestants ranked by total votes
            </Typography>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#ef364e" }} />
              </Box>
            ) : (
              <>
                <Stack direction="row" gap={1} flexWrap="wrap" mb={2.5} sx={{ minWidth: 0 }}>
                  <TextField
                    select
                    size="small"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    sx={{ ...filterSx, minWidth: { xs: "100%", sm: 150 }, flex: { xs: "1 1 100%", sm: "0 1 auto" } }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">All categories</MenuItem>
                    {(competition?.categories || []).map((c) => (
                      <MenuItem key={c.category} value={c.category}>
                        {c.category}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    value={eventFilter}
                    onChange={(e) => setEventFilter(e.target.value)}
                    sx={{ ...filterSx, minWidth: { xs: "100%", sm: 150 }, flex: { xs: "1 1 100%", sm: "0 1 auto" } }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">All events</MenuItem>
                    {(competition?.events || []).map((e) => (
                      <MenuItem key={e.id} value={e.id}>
                        {e.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                {lbLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress sx={{ color: "#ef364e" }} />
                  </Box>
                ) : (
                  <CompetitionLeaderboard
                    competitionId={id}
                    rows={rows}
                    votingEnabled={competition?.status === "running"}
                    categoryTitle={boardTitle}
                    competitionBanner={competition?.banner_img}
                  />
                )}
              </>
            )}
          </Box>
          <Footer />
        </Container>
      </Box>
    </>
  );
}
